import { useEffect, useRef } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { acquireScrollLock, releaseScrollLock } from './scroll-lock';
import { pushEscape } from './escape-stack';

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  children: ComponentChildren;
  heightVariant?: 'auto' | 'tall';
  scrimTestId?: string;
  ariaLabel?: string;
}

export function BottomSheet({ open, onClose, children, heightVariant = 'auto', scrimTestId, ariaLabel }: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    acquireScrollLock();
    const popEsc = pushEscape(onClose);
    const root = sheetRef.current;
    const focusable = root?.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    focusable?.focus();
    return () => {
      popEsc();
      releaseScrollLock();
    };
  }, [open, onClose]);

  if (!open) return null;
  const heightClass = heightVariant === 'tall' ? 'max-h-[60vh]' : 'max-h-[90vh]';
  return (
    <div class="fixed inset-0 z-40 pointer-events-auto">
      <div
        class="absolute inset-0 bg-black/50"
        onClick={onClose}
        data-testid={scrimTestId}
        aria-hidden="true"
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        class={`absolute inset-x-0 bottom-0 ${heightClass} bg-white text-[#222] rounded-t-2xl shadow-2xl pb-[env(safe-area-inset-bottom)] overflow-y-auto`}
      >
        <div class="w-12 h-1.5 bg-gray-300 rounded-full mx-auto my-3" aria-hidden="true" />
        {children}
      </div>
    </div>
  );
}
